import { accentWalkColour } from '@featit/shared/brand/colour-accents';

/**
 * F-0579 ACC-4e (I-1818, `docs/concept-colour-accents.md`): one gradient over every accent icon a
 * walk draws, worked out again whenever what it draws changes. Jay, 2026-10-20: "calculate the
 * gradient over all expanded icons. This means it needs recalculation on each expansion / collapse".
 *
 * A walk is an element marked `data-accent-walk` (the sidebar, its footer row, a settings group), or
 * any `role="menu"`. It owns the accent icons (`accentIconProps`) inside it that no nearer walk owns,
 * and icon *i* of *N* is coloured *i* / (*N* − 1) along the stops (`accentWalkColour`). A folded
 * child is not in the document, so it is not counted. An icon in no walk keeps its own stop.
 *
 * One observer for the whole document, installed once: a walk is drawn again only when an accent
 * icon or a walk inside it is added, removed or re-stopped, so a list's scroll costs a check per
 * changed node and nothing more.
 */

export const WALK_SELECTOR = '[data-accent-walk], [role="menu"]';
const ICON_SELECTOR = '[data-accent-icon]';

/** Colours one walk's own icons, in document order; returns how many it walked. */
export function walkAccents(root: Element): number {
  const icons = [...root.querySelectorAll<SVGElement | HTMLElement>(ICON_SELECTOR)].filter(
    (icon) => icon.parentElement?.closest(WALK_SELECTOR) === root,
  );
  icons.forEach((icon, position) => {
    const colour = accentWalkColour(position, icons.length);
    if (icon.style.getPropertyValue('--evx-accent-icon') !== colour) {
      icon.style.setProperty('--evx-accent-icon', colour);
    }
  });
  return icons.length;
}

/** Every walk a changed node belongs to or holds. */
function walksTouchedBy(node: Node, into: Set<Element>): void {
  const element = node instanceof Element ? node : node.parentElement;
  if (!element) return;
  const owner = element.closest(WALK_SELECTOR);
  if (owner) into.add(owner);
  if (node instanceof Element) {
    if (node.matches(WALK_SELECTOR)) into.add(node);
    node.querySelectorAll(WALK_SELECTOR).forEach((walk) => into.add(walk));
  }
}

function holdsAccent(node: Node): boolean {
  return (
    node instanceof Element &&
    (node.matches(ICON_SELECTOR) || node.querySelector(ICON_SELECTOR) !== null)
  );
}

let observer: MutationObserver | null = null;

/** Starts the document's walks; a second call does nothing. Returns the stop, for a test. */
export function installAccentWalks(doc: Document = document): () => void {
  if (observer || typeof MutationObserver === 'undefined') return () => undefined;
  doc.querySelectorAll(WALK_SELECTOR).forEach(walkAccents);
  observer = new MutationObserver((records) => {
    const walks = new Set<Element>();
    for (const record of records) {
      if (record.type === 'attributes') {
        walksTouchedBy(record.target, walks);
        continue;
      }
      const changed = [...record.addedNodes, ...record.removedNodes];
      if (
        !changed.some(
          (node) => holdsAccent(node) || (node instanceof Element && node.matches(WALK_SELECTOR)),
        )
      ) {
        continue;
      }
      // A removed node is out of the document: its walk is the one it was removed from.
      walksTouchedBy(record.target, walks);
      record.addedNodes.forEach((node) => walksTouchedBy(node, walks));
    }
    walks.forEach((walk) => {
      if (walk.isConnected) walkAccents(walk);
    });
  });
  observer.observe(doc.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-accent-icon', 'data-accent-walk', 'role'],
  });
  return () => {
    observer?.disconnect();
    observer = null;
  };
}
